// Identifier parsing helpers shared across scope/* modules. Handles
// dialect-specific quote forms (backticks, square brackets, double quotes)
// and identifier folding per ParseOptions. Pure function, no tree-sitter.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver — aliases.

import type {
  ParseOptions,
  SqlEngine,
  QualifiedName,
  SqlRange,
  IdentifierFolding,
  IdentifierQuoting,
} from '../../sql-grammar/model';

export interface ReadIdentResult {
  readonly raw: string;     // text as typed, including quotes
  readonly value: string;   // unquoted value (with escapes unescaped)
  readonly quoted: boolean;
  /** Per-token quoting style (v2 Slice H2). Picks the open-quote char of the
   *  source token; `'unquoted'` when the identifier was bare. */
  readonly quoting: IdentifierQuoting;
  readonly start: number;
  readonly end: number;     // index just past the identifier
}

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

// Read one identifier (plain or quoted) at `i`. Returns null if no identifier
// starts here. Does NOT cross whitespace or dots — caller joins parts via
// readQualifiedName.
export function readIdentifier(
  source: string,
  i: number,
  endExclusive: number,
  options: ParseOptions,
): ReadIdentResult | null {
  if (i >= endExclusive) return null;
  const c = source[i];
  const engine = options.engine;

  if ((engine === 'mysql' || engine === 'sqlite') && c === '`') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== '`') j++;
    const value = source.slice(i + 1, j);
    const end = j < endExclusive ? j + 1 : j;
    return { raw: source.slice(i, end), value, quoted: true, quoting: 'backtick', start: i, end };
  }

  if ((engine === 'mssql' || engine === 'sqlite') && c === '[') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== ']') j++;
    const value = source.slice(i + 1, j);
    const end = j < endExclusive ? j + 1 : j;
    return { raw: source.slice(i, end), value, quoted: true, quoting: 'bracket', start: i, end };
  }

  // T-SQL temp tables (#tmp / ##global) and variables (@tv / @@rowcount).
  // MSSQL-only arm: the sigil is one or two of the SAME char (`#`/`##`/`@`/
  // `@@`), then ≥1 IDENT_CONT char. The prefix stays IN value/raw (the object
  // name a user types is `#tmp`, and dotted resolution must match that), so
  // `quoted: false`, `quoting: 'unquoted'`, normal case-insensitive folding.
  // A lone sigil (or `##`/`@@`) with no following ident char is NOT an
  // identifier. The classify layer's own `#`-as-mysql-comment handling is a
  // different engine (mysql) so it can never collide with this arm.
  if (engine === 'mssql' && (c === '#' || c === '@')) {
    let j = i + 1;
    if (j < endExclusive && source[j] === c) j++; // second sigil: `##` / `@@`
    const contStart = j;
    while (j < endExclusive && IDENT_CONT.test(source[j])) j++;
    if (j === contStart) return null; // bare sigil, no name → not an identifier
    const raw = source.slice(i, j);
    return { raw, value: raw, quoted: false, quoting: 'unquoted', start: i, end: j };
  }

  if (c === '"' && engine !== 'mysql') {
    let j = i + 1;
    while (j < endExclusive) {
      if (source[j] === '"') {
        if (source[j + 1] === '"') { j += 2; continue; }
        break;
      }
      j++;
    }
    const value = source.slice(i + 1, j).replace(/""/g, '"');
    const end = j < endExclusive ? j + 1 : j;
    return { raw: source.slice(i, end), value, quoted: true, quoting: 'double-quote', start: i, end };
  }

  if (IDENT_START.test(c)) {
    let j = i + 1;
    while (j < endExclusive && IDENT_CONT.test(source[j])) j++;
    const raw = source.slice(i, j);
    return { raw, value: raw, quoted: false, quoting: 'unquoted', start: i, end: j };
  }

  return null;
}

// True iff a *bare* (unquoted) identifier can begin at `i` for the given
// engine. Mirrors readIdentifier's bare-arm entry condition: the ASCII
// ident-start class for every engine, plus the T-SQL temp/var sigils
// (`#`/`@`) in mssql. Callers pair this with a non-null readIdentifier result
// to tell bare identifiers apart from quoted forms — it replaces the inline
// `/[A-Za-z_]/.test(source[i])` guards that would otherwise make the mssql arm
// unreachable at those call sites.
export function isBareIdentStart(source: string, i: number, engine: SqlEngine): boolean {
  const c = source[i];
  if (c === undefined) return false;
  if (IDENT_START.test(c)) return true;
  return engine === 'mssql' && (c === '#' || c === '@');
}

export function defaultFolding(engine: SqlEngine): IdentifierFolding {
  switch (engine) {
    case 'oracle': return 'upper';
    case 'postgres': return 'lower';
    case 'mssql':
    case 'mysql':
    case 'sqlite':
    default: return 'case-insensitive';
  }
}

// Fold an identifier value per ParseOptions. Quoted identifiers preserve
// case in every engine except when folding is explicitly configured;
// callers that need engine-exact behavior (e.g. Oracle quoted vs unquoted)
// can pass the quoted flag and we'll preserve case accordingly.
export function foldIdentifier(
  value: string,
  quoted: boolean,
  options: ParseOptions,
): string {
  if (quoted) return value;
  const fold = options.identifierFolding ?? defaultFolding(options.engine);
  switch (fold) {
    case 'lower':
    case 'case-insensitive':
      return value.toLowerCase();
    case 'upper':
      return value.toUpperCase();
    case 'as-stored':
    default:
      return value;
  }
}

export interface ReadQualifiedNameResult {
  readonly qualified: QualifiedName;
  readonly range: SqlRange;
  /** String offsets of ONLY the terminal (object) token, delimiters included
   *  (e.g. `[accounts]` in `[db].[dbo].[accounts]`). Navigational rename uses
   *  this to rewrite just the object name, preserving schema/db qualification
   *  and any trailing alias. Line/column are zeroed — assemble via
   *  `rangeFromOffsets`. */
  readonly terminalRange: { readonly startOffset: number; readonly endOffset: number };
  /** Number of dot-separated parts the source reference carried (1 = bare,
   *  2 = schema.name, 3 = db.schema.name, ≥4 = server.db.schema.name). MSSQL
   *  4-part linked-server references collapse the server component in
   *  `qualified` (no `server` field in the model), so consumers that must
   *  distinguish a genuine 3-part identity from a server-qualified 4-part one
   *  (rename ambiguity gating) read this instead. */
  readonly partCount: number;
}

// Read a possibly-dotted identifier chain: name, schema.name, db.schema.name.
// MSSQL 4-part `server.db.schema.name` collapses the server into database
// for v1 (dblink stays unset here; the dotted-base resolver owns 4-part
// unpacking). Returns null if no identifier at `i`. Range line/column are
// zeroed — callers that need them assemble via rangeFromOffsets.
export function readQualifiedName(
  source: string,
  i: number,
  endExclusive: number,
  options: ParseOptions,
): ReadQualifiedNameResult | null {
  const first = readIdentifier(source, i, endExclusive, options);
  if (!first) return null;
  const parts: ReadIdentResult[] = [first];
  let pos = first.end;

  while (pos < endExclusive && source[pos] === '.') {
    pos++;
    const next = readIdentifier(source, pos, endExclusive, options);
    if (!next) break;
    parts.push(next);
    pos = next.end;
  }

  const last = parts[parts.length - 1];
  const schema = parts.length >= 2 ? parts[parts.length - 2] : null;
  const database = parts.length >= 3 ? parts[parts.length - 3] : null;

  const qualified: QualifiedName = {
    name: last.value,
    foldedName: foldIdentifier(last.value, last.quoted, options),
    quoted: parts.some((p) => p.quoted),
    quoting: last.quoting,
    raw: source.slice(i, pos),
  };
  if (schema) {
    qualified.schema = schema.value;
    qualified.foldedSchema = foldIdentifier(schema.value, schema.quoted, options);
  }
  if (database) {
    qualified.database = database.value;
    qualified.foldedDatabase = foldIdentifier(database.value, database.quoted, options);
  }

  const range: SqlRange = {
    startOffset: i,
    endOffset: pos,
    startLine: 0, startColumn: 0, endLine: 0, endColumn: 0,
  };
  return {
    qualified,
    range,
    terminalRange: { startOffset: last.start, endOffset: last.end },
    partCount: parts.length,
  };
}
